/**
 * NDJSON-stream-reader for /api/ai/generate.
 *
 * Backend sender én JSON-linje per fase (Newline-Delimited JSON). Denne
 * helperen tar en Response og en callback, og kaller callback per event.
 *
 * Bruk:
 *   const response = await fetch('/api/ai/generate', { ... });
 *   const result = await readNdjsonStream(response, (event) => {
 *     if (event.phase !== 'result') setProgress(event);
 *   });
 *   // result.data inneholder generated_plan, pre_analysis, compression, etc.
 */

export interface NdjsonProgressEvent {
  phase: string;
  status: 'started' | 'completed' | 'skipped' | 'failed';
  label?: string;
  timestamp: number;
  [key: string]: any;
}

export interface NdjsonResultEvent {
  phase: 'result';
  status: 'completed';
  data: any;
  timestamp: number;
}

export interface NdjsonErrorEvent {
  phase: 'error';
  status: 'failed';
  error: string;
  statusCode?: number;
  timestamp: number;
}

export type NdjsonEvent = NdjsonProgressEvent | NdjsonResultEvent | NdjsonErrorEvent;

/**
 * Les en NDJSON-stream og kall onEvent for hver event. Returnerer final result-data
 * når 'result'-event mottas, eller kaster Error hvis 'error'-event mottas.
 */
export async function readNdjsonStream(
  response: Response,
  onEvent: (event: NdjsonEvent) => void
): Promise<any> {
  if (!response.ok && response.status !== 200) {
    // Hvis backend feilet før streaming startet, har vi JSON-feilrespons
    try {
      const errJson = await response.json();
      throw new Error(errJson.error || errJson.message || `HTTP ${response.status}`);
    } catch (e) {
      if (e instanceof Error) throw e;
      throw new Error(`HTTP ${response.status}`);
    }
  }

  if (!response.body) {
    throw new Error('Response body mangler (streaming støttes ikke)');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: any = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split på newline, behold ufullstendig siste linje i buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        let event: NdjsonEvent;
        try {
          event = JSON.parse(trimmed) as NdjsonEvent;
        } catch (parseErr) {
          console.warn('[NdjsonStream] Ugyldig JSON-linje, hopper over:', trimmed.slice(0, 100));
          continue;
        }

        if (event.phase === 'result') {
          finalResult = (event as NdjsonResultEvent).data;
          onEvent(event);
        } else if (event.phase === 'error') {
          const errEvent = event as NdjsonErrorEvent;
          onEvent(event);
          throw new Error(errEvent.error || 'Ukjent serverfeil');
        } else {
          onEvent(event);
        }
      }
    }

    // Sjekk siste linje hvis ikke avsluttet med newline
    const lastLine = buffer.trim();
    if (lastLine) {
      try {
        const event = JSON.parse(lastLine) as NdjsonEvent;
        if (event.phase === 'result') {
          finalResult = (event as NdjsonResultEvent).data;
        }
        onEvent(event);
      } catch {
        // ignorer ufullstendig data
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!finalResult) {
    throw new Error('Stream avsluttet uten result-event');
  }

  return finalResult;
}
